import { useCallback, useMemo } from 'react';
import {
  hasPermission,
  type PermissionKey,
  type Scope,
  type ScopedPermission,
} from '@shared/permissions';
import type { Role } from '@shared/enums';
import { useAppSelector } from '@/store';
import type { SessionUser } from '@/store/authSlice';

/**
 * The current user's permissions, from the real session.
 *
 * The list comes from `/auth/me`, which the server resolves — role grants plus the
 * manager and department-head grants derived from organizational facts. The client
 * never infers permissions from a role name.
 *
 * This is a UI HINT. Every action it reveals is still authorized server-side on the
 * request; hiding a control has never been authorization.
 */

export interface Permissions {
  user: SessionUser | null;
  role: Role | null;
  keys: ScopedPermission[];
  can: (key: PermissionKey, scope?: Scope) => boolean;
  isAuthenticated: boolean;
}

export function usePermissions(): Permissions {
  const user = useAppSelector((s) => s.auth.user);
  const status = useAppSelector((s) => s.auth.status);

  const keys = useMemo(() => (user?.permissions ?? []) as ScopedPermission[], [user]);

  const can = useCallback(
    (key: PermissionKey, scope?: Scope) => hasPermission(keys, key, scope),
    [keys],
  );

  return {
    user,
    role: (user?.role as Role | undefined) ?? null,
    keys,
    can,
    isAuthenticated: status === 'authenticated',
  };
}
